import {
  decryptPayload,
  encryptPayload,
  looksEncrypted,
  payloadEncryptionEnabled,
} from './payload-crypto'

/**
 * Typed API client for the operator panel.
 *
 * The single rule, inherited from the public app: every monetary field arrives as a
 * DECIMAL STRING and becomes a `bigint` here, at the boundary. Nothing downstream ever
 * sees a JSON number for a token amount — a uint256 does not survive an IEEE-754
 * double, and an exit-obligation figure that is quietly wrong in its last digits is
 * worse than one that fails loudly. That matters more here than anywhere: these numbers
 * are what an operator decides on.
 */

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3101'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let accessToken: string | null = null

/** Access token is held in memory only — never localStorage, which XSS can read. */
export function setAccessToken(token: string | null): void {
  accessToken = token
}

interface Envelope<T> {
  data: T
  meta?: { at: number; cached?: boolean; blockNumber?: string }
}

/**
 * De-duplicated silent refresh.
 *
 * The access token lives 15 minutes and `useAuth` rotates it every 12 — but a laptop
 * that sleeps through that window wakes with a dead token, and then every query 401s
 * until the next tick. On this panel that is worse than an annoyance: the ops board
 * polls, so the operator would watch live figures turn into errors during exactly the
 * incident they opened the page for.
 *
 * The in-flight promise is shared, so ten simultaneous 401s trigger one refresh rather
 * than ten competing ones — each of which would rotate the cookie and invalidate the
 * others.
 */
let refreshInFlight: Promise<boolean> | null = null

async function silentRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      })
      if (!res.ok) return false
      const body = (await res.json()) as Envelope<{ accessToken: string }>
      accessToken = body.data.accessToken
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so concurrent callers all observe this result.
      setTimeout(() => {
        refreshInFlight = null
      }, 0)
    }
  })()
  return refreshInFlight
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const headers = new Headers(init?.headers)
  headers.set('accept', 'application/json')
  if (init?.body) headers.set('content-type', 'application/json')
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)

  let body = init?.body
  if (payloadEncryptionEnabled && typeof body === 'string') {
    body = JSON.stringify(await encryptPayload(body))
  }

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    ...(body !== undefined ? { body } : {}),
    headers,
    credentials: 'include', // refresh cookie
  })

  // Refresh-and-replay exactly once. `isRetry` stops a permanently-rejecting endpoint
  // from ping-ponging, and the auth routes are excluded so a failing refresh cannot
  // recurse into itself.
  if (response.status === 401 && !isRetry && !path.startsWith('/api/v1/auth/')) {
    if (await silentRefresh()) return request<T>(path, init, true)
  }

  const text = await response.text()
  let payload: unknown
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    throw new ApiError('INTERNAL', 'Server returned a malformed response.', response.status)
  }

  // Unwrap the envelope if the server encrypted this response. Detected from the body
  // shape rather than the mode flag, so a client and server that disagree about whether
  // encryption is on still interoperate during a rolling deploy.
  if (looksEncrypted(payload)) {
    const plaintext = await decryptPayload(payload)
    if (plaintext === null) {
      throw new ApiError('INTERNAL', 'Could not decrypt the server response.', response.status)
    }
    try {
      payload = JSON.parse(plaintext)
    } catch {
      throw new ApiError('INTERNAL', 'Server returned a malformed response.', response.status)
    }
  }

  if (!response.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error
    throw new ApiError(
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Request failed.',
      response.status,
      err?.details,
    )
  }

  return (payload as Envelope<T>).data
}

/** Decimal string → bigint. Anything unparseable becomes 0n rather than NaN. */
function big(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value)
  if (typeof value === 'number') return BigInt(Math.trunc(value))
  return 0n
}

// ─────────────────────────── Auth ───────────────────────────

export async function fetchNonce(): Promise<{
  nonce: string
  domain: string
  statement: string
  chainId: number
}> {
  return request('/api/v1/auth/nonce')
}

export interface SessionResponse {
  address: string
  isAdmin: boolean
  accessToken: string
  expiresInSeconds: number
}

export async function verifySignature(
  message: string,
  signature: string,
): Promise<SessionResponse> {
  return request('/api/v1/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ message, signature }),
  })
}

export async function refreshSession(): Promise<{
  address: string
  isAdmin: boolean
  accessToken: string
}> {
  return request('/api/v1/auth/refresh', { method: 'POST' })
}

export async function logout(): Promise<void> {
  await request('/api/v1/auth/logout', { method: 'POST' })
  setAccessToken(null)
}

// ─────────────────────────── Ops board ───────────────────────────

export type RbsZone = 'BUY' | 'IDLE' | 'MINT'

export interface OpsAlert {
  level: 'critical' | 'warning'
  message: string
}

/** One week of the four-week exit pipeline. Bucketed server-side, in Postgres. */
export interface ExitPipelineWeek {
  week: number
  from: string
  to: string
  /** What the protocol owes to exits completing in this window. */
  scheduledExitUsdt: bigint
  maturingBonds: number
  maturingPrincipal: bigint
}

export interface OpsBoard {
  snapshot: {
    /** WAD. 1.0e18 is parity between market price and backing. */
    premiumIndex: bigint
    backingPerCdx: bigint
    marketPrice: bigint
    rbsZone: RbsZone
    /** WAD. LCR_TARGET is 1.5e18; below 1.0e18 not every scheduled exit can clear. */
    lcr: bigint
    lcrStatus: 'healthy' | 'warning' | 'critical'
    mintHeadroom: bigint
    blockNumber: bigint
  }
  exitPipeline: ExitPipelineWeek[]
  rbsReserve: {
    usdt: bigint
    /** WAD fraction of the high-water mark still held. `depleted` is < 0.2e18. */
    remainingFraction: bigint
    depleted: boolean
  }
  staleQueueCount: number
  readOnly: boolean
  alerts: OpsAlert[]
}

export async function fetchOpsBoard(): Promise<OpsBoard> {
  const raw = await request<Record<string, unknown>>('/api/v1/admin/ops')
  const s = raw.snapshot as Record<string, unknown>
  const r = raw.rbsReserve as Record<string, unknown>

  return {
    snapshot: {
      premiumIndex: big(s.premiumIndex),
      backingPerCdx: big(s.backingPerCdx),
      marketPrice: big(s.marketPrice),
      rbsZone: s.rbsZone as RbsZone,
      lcr: big(s.lcr),
      lcrStatus: s.lcrStatus as OpsBoard['snapshot']['lcrStatus'],
      mintHeadroom: big(s.mintHeadroom),
      blockNumber: big(s.blockNumber),
    },
    exitPipeline: (raw.exitPipeline as Record<string, unknown>[]).map((w) => ({
      week: Number(w.week),
      from: String(w.from),
      to: String(w.to),
      scheduledExitUsdt: big(w.scheduledExitUsdt),
      maturingBonds: Number(w.maturingBonds),
      maturingPrincipal: big(w.maturingPrincipal),
    })),
    rbsReserve: {
      usdt: big(r.usdt),
      remainingFraction: big(r.remainingFraction),
      depleted: Boolean(r.depleted),
    },
    staleQueueCount: Number(raw.staleQueueCount),
    readOnly: Boolean(raw.readOnly),
    alerts: raw.alerts as OpsAlert[],
  }
}

// ─────────────────────────── Kill switch ───────────────────────────

/**
 * Current switch state.
 *
 * Not admin-gated on the server — every visitor to the public app reads it to render
 * the maintenance banner — so this one call works before sign-in. That is what lets the
 * panel show the protocol's state on its login screen.
 */
export async function fetchReadOnly(): Promise<boolean> {
  const raw = await request<{ readOnly: boolean }>('/api/v1/admin/readonly')
  return raw.readOnly
}

/**
 * Flip the kill switch.
 *
 * `reason` is mandatory server-side (3–500 characters) because the audit row is the
 * whole point: a flip with no attributable explanation is exactly the record an
 * incident review cannot use.
 */
export async function setReadOnly(
  enabled: boolean,
  reason: string,
): Promise<{ readOnly: boolean; reason: string }> {
  return request('/api/v1/admin/readonly', {
    method: 'POST',
    body: JSON.stringify({ enabled, reason }),
  })
}

// ─────────────────────────── Withdrawal queue ───────────────────────────

export interface PendingWithdrawal {
  id: string
  /** The wallet that owns the balance. */
  address: string
  /** Where the payout goes. Equal to `address` for anything created through the API. */
  to: string
  /** Gross bCDX, in wei. */
  amount: bigint
  status: 'requested' | 'approved'
  requestedAt: string
  /**
   * True when `to` differs from `address`.
   *
   * There is no destination parameter anywhere in the request path, so this is always
   * false for a row the API created. A true here means something wrote the row another
   * way, and it must not be approved without finding out what.
   */
  destinationMismatch: boolean
}

export async function fetchWithdrawalQueue(): Promise<{
  count: number
  withdrawals: PendingWithdrawal[]
}> {
  const raw = await request<{ count: number; withdrawals: Record<string, unknown>[] }>(
    '/api/v1/admin/withdrawals',
  )
  return {
    count: raw.count,
    withdrawals: raw.withdrawals.map((w) => ({
      id: String(w.id),
      address: String(w.address),
      to: String(w.to),
      amount: big(w.amount),
      status: w.status as PendingWithdrawal['status'],
      requestedAt: String(w.requestedAt),
      destinationMismatch: Boolean(w.destinationMismatch),
    })),
  }
}

/**
 * Release a withdrawal to the payout executor.
 *
 * The executor only ever pays rows in `approved`, and nothing a user can reach writes
 * that status — so this call is the single point at which the protocol decides to move
 * money. Treat a 200 as "queued for payment", not "paid".
 */
export async function approveWithdrawal(
  id: string,
): Promise<{ id: string; status: string; amount: string }> {
  return request(`/api/v1/admin/withdrawals/${id}/approve`, { method: 'POST' })
}

/** Decline a withdrawal. The reason is shown to the user, so write it for them. */
export async function rejectWithdrawal(
  id: string,
  reason: string,
): Promise<{ id: string; status: string; reason: string }> {
  return request(`/api/v1/admin/withdrawals/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}
